// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {useTranslation} from "react-i18next";
import {ConfEnv} from "../../config/config";
import privateRequest from "../../Infra/http/private.request";
import useCurrencyStore from "../../store/useCurrency.store";

const overlayContainerCls =
  "fixed inset-0 bg-black/80 backdrop-blur-sm z-[100000] flex justify-center items-center p-5 max-md:p-2.5 max-[480px]:p-0";
const gameModalCls =
  "bg-[var(--color-surface)] border-2 border-[var(--color-text-inverse)] rounded-xl shadow-[0_20px_40px_rgba(0,0,0,0.3)] w-[90vw] max-w-[1200px] h-[80vh] max-h-[800px] flex flex-col overflow-hidden transition-all duration-300 max-md:w-[95vw] max-md:h-[85vh] max-[480px]:w-screen max-[480px]:h-[90vh] max-[480px]:rounded-none";
const minimizedCls =
  "!w-[var(--size-75)] !h-[var(--size-15)] !fixed !bottom-5 !right-5 !bg-[var(--color-surface-2)] !border !border-[var(--color-gray-4)] !shadow-[var(--shadow-md)] max-md:!w-[250px] max-md:!h-[var(--size-12-5)] max-md:!bottom-2.5 max-md:!right-2.5";
const modalHeaderCls =
  "bg-[linear-gradient(135deg,var(--color-surface-2),var(--color-surface))] border-b border-[var(--color-text-inverse)] px-4 py-3 flex justify-between items-center min-h-[var(--size-15)] max-md:px-3 max-md:py-2.5 max-md:min-h-[var(--size-12-5)]";
const gameInfoCls = "flex flex-col gap-0.5";
const gameTitleCls = "text-base font-semibold text-[var(--color-text)] m-0 leading-tight max-md:text-sm";
const gameProviderCls =
  "text-xs text-[var(--color-text-tertiary)] uppercase tracking-[0.5px] max-md:text-[var(--font-caption)]";
const modalControlsCls = "flex gap-2";
const controlButtonCls =
  "bg-transparent border border-[var(--color-gray-4)] text-[var(--color-text-secondary)] w-8 h-8 rounded-md cursor-pointer flex items-center justify-center text-base font-bold transition-all duration-200 max-md:w-7 max-md:h-7 max-md:text-sm";
const minimizeButtonCls = `${controlButtonCls} hover:bg-[var(--color-gray-4)] hover:border-[var(--color-gray-4)] hover:text-[var(--color-text)]`;
const closeButtonCls = `${controlButtonCls} hover:bg-[var(--color-danger)] hover:border-[var(--color-danger)] hover:text-[var(--color-text)]`;
const modalContentCls = "flex-1 relative bg-[var(--color-bg)] overflow-hidden";
const gameIframeCls = "w-full h-full border-none bg-[var(--color-bg)]";
const loadingContainerCls =
  "absolute inset-0 bg-[var(--color-surface)] flex flex-col justify-center items-center gap-4";
const loadingSpinnerCls =
  "w-10 h-10 border-[3px] border-[var(--color-text-inverse)] border-t-[var(--color-info)] rounded-full motion-safe:animate-payku-spin";
const loadingTextCls = "text-[var(--color-text-secondary)] text-sm m-0";
const errorContainerCls =
  "absolute inset-0 bg-[var(--color-surface)] flex flex-col justify-center items-center gap-4 p-5";
const errorIconCls = "text-[var(--font-display)]";
const errorTextCls = "text-[var(--color-text-secondary)] text-sm text-center m-0 max-w-[var(--size-75)]";
const retryButtonCls =
  "bg-[var(--color-info)] border-none text-white px-5 py-2.5 rounded-md cursor-pointer text-sm transition-colors duration-200 hover:bg-[var(--color-info)]";

const ParallelGameModal = ({game, onClose, onMinimize, isMinimized}) => {
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [gameUrl, setGameUrl] = useState(null);
  const iframeRef = useRef(null);

  // Direct API call for parallel game initialization (independent of main game store)
  const initializeParallelGame = async (provider, gameId) => {
    const selectedCurrency = useCurrencyStore.getState().selectedCurrency;
    const currencyParam = selectedCurrency ? selectedCurrency.id : "VES";

    const url = `${ConfEnv.API_URL.GAMES_INIT}/${provider}/${gameId}?currency=${currencyParam}`;
    const response = await privateRequest.get(url);
    const {gameUrl: newGameUrl = null} = response?.data?.data || {};

    return newGameUrl;
  };

  // Initialize the game when component mounts or game changes
  useEffect(() => {
    const initializeGame = async () => {
      setIsLoading(true);
      setHasError(false);
      setGameUrl(null);

      try {
        const newGameUrl = await initializeParallelGame(game.provider, game.id);

        if (newGameUrl) {
          setGameUrl(newGameUrl);
          setIsLoading(false);
        } else {
          setHasError(true);
          setIsLoading(false);
        }
      } catch (_error) {
        setHasError(true);
        setIsLoading(false);
      }
    };

    if (game?.provider && game?.id) {
      initializeGame();
    }
  }, [game.provider, game.id]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleOverlayClick = e => {
    // Close modal if clicking on overlay background
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className={overlayContainerCls}
      onClick={handleOverlayClick}
    >
      <div className={`${gameModalCls} ${isMinimized ? minimizedCls : ""}`}>
        {/* Modal Header */}
        <div className={modalHeaderCls}>
          <div className={gameInfoCls}>
            <h3 className={gameTitleCls}>{game.name || game.game_name || t("unknownGame", "Unknown Game")}</h3>
            <span className={gameProviderCls}>{game.provider || t("unknownProvider", "Unknown")}</span>
          </div>

          <div className={modalControlsCls}>
            <button
              type="button"
              className={minimizeButtonCls}
              onClick={onMinimize}
              title={isMinimized ? t("maximize", "Maximize") : t("minimize", "Minimize")}
            >
              {isMinimized ? "□" : "−"}
            </button>
            <button
              type="button"
              className={closeButtonCls}
              onClick={onClose}
              title={t("close", "Close")}
            >
              ×
            </button>
          </div>
        </div>

        {/* Modal Content */}
        {!isMinimized && (
          <div className={modalContentCls}>
            {isLoading && (
              <div className={loadingContainerCls}>
                <div className={loadingSpinnerCls}></div>
                <p className={loadingTextCls}>{t("loadingGame", "Loading game...")}</p>
              </div>
            )}

            {hasError && (
              <div className={errorContainerCls}>
                <div className={errorIconCls}>⚠️</div>
                <p className={errorTextCls}>{t("gameLoadError", "Failed to load game. Please try again.")}</p>
                <button
                  type="button"
                  className={retryButtonCls}
                  onClick={async () => {
                    setHasError(false);
                    setIsLoading(true);
                    try {
                      const newGameUrl = await initializeParallelGame(game.provider, game.id);
                      if (newGameUrl) {
                        setGameUrl(newGameUrl);
                        setIsLoading(false);
                      } else {
                        setHasError(true);
                        setIsLoading(false);
                      }
                    } catch (_error) {
                      setHasError(true);
                      setIsLoading(false);
                    }
                  }}
                >
                  {t("retry", "Retry")}
                </button>
              </div>
            )}

            {gameUrl && (
              <iframe
                ref={iframeRef}
                src={gameUrl}
                title={game.name || game.game_name}
                className={gameIframeCls}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                allow="clipboard-read; clipboard-write; fullscreen; screen-wake-lock; accelerometer; gyroscope; magnetometer; microphone; camera; geolocation"
                style={{
                  display: isLoading || hasError ? "none" : "block",
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render modal using portal to ensure it appears on top
  return ReactDOM.createPortal(modalContent, document.body);
};

ParallelGameModal.propTypes = {
  game: PropTypes.shape({
    id: PropTypes.string.isRequired,
    provider: PropTypes.string.isRequired,
    name: PropTypes.string,
    game_name: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onMinimize: PropTypes.func.isRequired,
  isMinimized: PropTypes.bool,
};

ParallelGameModal.defaultProps = {
  isMinimized: false,
};

export default ParallelGameModal;
